import type React from 'react'
import { theme } from '../../theme'

interface CardProps {
  children: React.ReactNode
  title?: string
  subtitle?: string
  icon?: React.ReactNode
  actions?: React.ReactNode
  padding?: boolean
  hover?: boolean
  onClick?: () => void
  style?: React.CSSProperties
  className?: string
  size?: 'small' | 'medium' | 'large'
}

export const Card: React.FC<CardProps> = ({
  children,
  title,
  subtitle,
  icon,
  actions,
  padding = true,
  hover = false,
  onClick,
  style,
  className,
  size = 'medium',
}) => {
  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.lg,
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    transition: `all ${theme.animations.duration.normal}ms ${theme.animations.easing.default}`,
  }

  const hoverStyle: React.CSSProperties = hover ? {
    boxShadow: theme.shadows.medium,
  } : {}

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.lg,
    borderBottom: `1px solid ${theme.colors.border}`,
  }

  const headerContentStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
    flex: 1,
  }

  const iconStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.primary,
    fontSize: '20px',
  }

  const headerTextStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    flex: 1,
  }

  const titleStyle: React.CSSProperties = {
    ...theme.typography.h3,
    color: theme.colors.text.primary,
  }

  const subtitleStyle: React.CSSProperties = {
    ...theme.typography.body,
    color: theme.colors.text.secondary,
  }

  const contentStyle: React.CSSProperties = {
    padding: padding ? theme.spacing.lg : 0,
  }

  return (
    <div
      style={{ ...cardStyle, ...style }}
      className={className}
      onMouseEnter={(e) => hover && Object.assign(e.currentTarget.style, hoverStyle)}
      onMouseLeave={(e) => hover && Object.assign(e.currentTarget.style, { boxShadow: 'none' })}
      onClick={onClick}
    >
      {(title || subtitle || icon || actions) && (
        <div style={headerStyle}>
          <div style={headerContentStyle}>
            {icon && <div style={iconStyle}>{icon}</div>}
            <div style={headerTextStyle}>
              {title && <div style={titleStyle}>{title}</div>}
              {subtitle && <div style={subtitleStyle}>{subtitle}</div>}
            </div>
          </div>
          {actions && <div>{actions}</div>}
        </div>
      )}
      <div style={contentStyle}>
        {children}
      </div>
    </div>
  )
}
